import { useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import { ICertificate } from "../../models/certificate";
import { IntlShape, useIntl } from "react-intl";
import { toast } from "../..";
import { printCertificate } from "../../api/certificates";
import { formatDate } from "../../models/common";

type Props = {
    open: boolean;
    closeModal: () => void;
    certificate: ICertificate;
};

export const PrintCertificateModal = ({ open, closeModal, certificate }: Props) => {
    const [loading, setLoading] = useState(false);
    const intl = useIntl();
    const lastPublishDate = !!certificate.batchId
        ? formatLastPublishDate(certificate.batchId)
        : intl.formatMessage({ id: "never" }).toLowerCase();

    const handlePrintCertificate = async () => {
        setLoading(true);
        try {
            await printCertificate(certificate.qualificationID);
            toast(intl.formatMessage({ id: "certSentToPrint" }), true);
        } catch {
            toast(intl.formatMessage({ id: "failedToSendCert" }), false);
        } finally {
            setLoading(false);
            closeModal();
        }
    };

    return (
        <Modal
            open={open}
            onClose={closeModal}
            size="tiny"
            dimmer="inverted"
            closeOnDimmerClick={!loading}
        >
            <Modal.Header>{intl.formatMessage({ id: "confirmPrint" })}</Modal.Header>
            <Modal.Content>
                <p>
                    {intl.formatMessage({ id: "lastPublishDate" })}:&nbsp;{lastPublishDate}
                </p>
                {!!certificate.batchId && <p>{getPrintStatus(certificate, intl)}</p>}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    negativ
                    onClick={closeModal}
                    content={intl.formatMessage({ id: "cancel" })}
                    disabled={loading}
                />
                <Button
                    primary
                    onClick={handlePrintCertificate}
                    content={intl.formatMessage({ id: "confirm" })}
                    loading={loading}
                    disabled={loading}
                />
            </Modal.Actions>
        </Modal>
    );
};

const formatLastPublishDate = (batchId: string) => {
    const date = new Date(
        +batchId.substring(0, 4),
        +batchId.substring(4, 6) - 1,
        +batchId.substring(6, 8),
        +batchId.substring(8, 10),
        +batchId.substring(10, 12),
        +batchId.substring(12, 14)
    );
    return formatDate(date.toISOString(), true);
};

const getPrintStatus = (certificate: ICertificate, intl: IntlShape) => {
    if (!certificate.printAcknowledged) return intl.formatMessage({ id: "pending" });
    else if (!!certificate.printFeedback)
        return intl.formatMessage({ id: "failure" }) + ` (${certificate.printFeedback})`;
    else return intl.formatMessage({ id: "success" });
};
